import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { PiArrowClockwiseBold, PiCaretDownBold } from 'react-icons/pi';
import { CHIP_MENU_ALIGN_RIGHT, ChipMenu, TOOLTIP_PLACEMENT_LEFT } from '@aibuild/ui';
import type { ViewPreset } from '../cameraRig';
import type { CubeLayout } from '../viewport';
import { usePortalContainer } from '../environment';
import ChromeButton from './ChromeButton';
import { useChromeLayout } from './ChromeContext';
import { messages } from './messages';
import { usePopover } from './usePopover';
import { CubeButton, CubeOverlay } from './ViewCubeControls.styled';

const PROJECTIONS: readonly { preset: ViewPreset; label: keyof typeof messages }[] = [
  { preset: 'perspective', label: 'viewPerspective' },
  { preset: 'isometric', label: 'viewIsometric' },
];

const FACES: readonly { preset: ViewPreset; label: keyof typeof messages }[] = [
  { preset: 'front', label: 'viewFront' },
  { preset: 'back', label: 'viewBack' },
  { preset: 'right', label: 'viewRight' },
  { preset: 'left', label: 'viewLeft' },
  { preset: 'top', label: 'viewTop' },
  { preset: 'bottom', label: 'viewBottom' },
];

export interface ViewCubeControlsProps {
  layout: CubeLayout;
  /** The pointer is over the cube the canvas draws under these buttons. */
  cubeHovered: boolean;
  onResetView: () => void;
  onPreset: (preset: ViewPreset) => void;
}

/** Reset view and the view options menu, shown over the cube while it is pointed at. */
const ViewCubeControls: React.FC<ViewCubeControlsProps> = ({ layout, cubeHovered, onResetView, onPreset }) => {
  const intl = useIntl();
  const portal = usePortalContainer();
  const { coarse } = useChromeLayout();
  const optionsRef = useRef<HTMLButtonElement>(null);
  const options = usePopover(optionsRef);
  const [hovered, setHovered] = useState(false);
  const shown = coarse || cubeHovered || hovered || options.open;
  const optionsLabel = intl.formatMessage(messages.viewOptions);
  const pick = (preset: ViewPreset) => {
    onPreset(preset);
    options.close();
  };
  const item = ({ preset, label }: { preset: ViewPreset; label: keyof typeof messages }) => (
    <ChipMenu.Item key={preset} onClick={() => pick(preset)} dataTestId={`model-viewer__view-${preset}`}>
      {intl.formatMessage(messages[label])}
    </ChipMenu.Item>
  );
  return (
    <CubeOverlay
      $size={layout.size}
      $margin={layout.margin}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <CubeButton $corner="top-left" $shown={shown}>
        <ChromeButton
          label={intl.formatMessage(messages.resetView)}
          icon={<PiArrowClockwiseBold />}
          tooltipPlacement={TOOLTIP_PLACEMENT_LEFT}
          onClick={onResetView}
          testId="model-viewer__reset-view"
        />
      </CubeButton>
      <CubeButton $corner="bottom-right" $shown={shown}>
        <ChromeButton
          ref={optionsRef}
          label={optionsLabel}
          icon={<PiCaretDownBold />}
          pressed={options.open}
          tooltipPlacement={TOOLTIP_PLACEMENT_LEFT}
          onClick={options.toggle}
          onMouseDown={options.keepOpenOnPress}
          testId="model-viewer__view-options"
        />
      </CubeButton>
      {options.open && (
        <ChipMenu
          onClose={options.close}
          portal
          portalContainer={portal}
          anchorRef={optionsRef}
          align={CHIP_MENU_ALIGN_RIGHT}
          width={180}
          ariaLabel={optionsLabel}
          dataTestId="model-viewer__view-menu"
        >
          <ChipMenu.List>
            {PROJECTIONS.map(item)}
            <ChipMenu.Separator />
            {FACES.map(item)}
          </ChipMenu.List>
        </ChipMenu>
      )}
    </CubeOverlay>
  );
};

export default ViewCubeControls;
